import type { SortingState } from '@tanstack/react-table';

/**
 * Converts TanStack Table sorting state to Payload CMS sort parameter format
 * Returns a properly typed sort parameter for Payload API that excludes symbol keys
 *
 * @template T The entity type (e.g. Customer, Contract)
 * @param sorting The TanStack sorting state
 * @param relationshipFields Optional mapping for relationship fields to sort by specific fields
 * @returns A properly typed sort parameter for Payload API
 */
export function getSortParam<T>(
  sorting: SortingState,
  relationshipFields?: Record<string, string>
): `-${Exclude<keyof T, symbol>}` | keyof T | undefined {
  if (!sorting.length) return undefined;

  // If there's only one sort item, return it directly with appropriate format
  if (sorting.length === 1) {
    const sort = sorting[0];

    // If this is a relationship field and we have a mapping
    if (relationshipFields && sort.id in relationshipFields) {
      // We need to cast this as the return type to maintain the function signature
      // Even though it's a string that may not directly correspond to a keyof T
      return sort.desc
        ? (`-${relationshipFields[sort.id]}` as `-${Exclude<keyof T, symbol>}`)
        : (relationshipFields[sort.id] as keyof T);
    }

    // Default behavior
    return sort.desc
      ? (`-${sort.id as Exclude<keyof T, symbol>}` as `-${Exclude<keyof T, symbol>}`)
      : (sort.id as keyof T);
  }

  // For multiple sort items, we use the first one only to maintain type compatibility
  const sort = sorting[0];

  // If this is a relationship field and we have a mapping
  if (relationshipFields && sort.id in relationshipFields) {
    // We need to cast this as the return type to maintain the function signature
    return sort.desc
      ? (`-${relationshipFields[sort.id]}` as `-${Exclude<keyof T, symbol>}`)
      : (relationshipFields[sort.id] as keyof T);
  }

  // Default behavior
  return sort.desc
    ? (`-${sort.id as Exclude<keyof T, symbol>}` as `-${Exclude<keyof T, symbol>}`)
    : (sort.id as keyof T);
}
